import { useCallback, useEffect, useMemo, useState } from "react";
import { Spin, message } from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import { useUiState } from "../../state";
import { textMap } from "../../i18n";
import { fetchStudioScansList } from "../../services/compliance";
import { ScanHistoryEmptyStatus } from "./ScanHistoryEmptyStatus";
import { StudioScanRecordsPanel } from "./StudioScanRecordsPanel";
import { loadStudioScanHistory, type StoredStudioScan } from "./studioScanHistoryStorage";
import {
  mergeServerScansWithLocal,
  storedScanFromListItem,
} from "./studioServerScan";
import "../../styles/studio-workbench.css";

export function StudioScansListPage() {
  const { lang } = useUiState();
  const t = textMap[lang];
  const navigate = useNavigate();
  const location = useLocation();
  const [rows, setRows] = useState<StoredStudioScan[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const items = await fetchStudioScansList({ limit: 200 });
      const mapped = items.map(storedScanFromListItem);
      setRows(mergeServerScansWithLocal(mapped));
    } catch {
      setRows(mergeServerScansWithLocal(loadStudioScanHistory()));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load, location.key, location.pathname]);

  const openDetail = (row: StoredStudioScan) => {
    navigate(`/studio/scans/${encodeURIComponent(row.scanDisplayId)}`, {
      state: { studioDetailBack: "records" as const },
    });
  };

  const body = useMemo(() => {
    if (loading) {
      return (
        <div style={{ padding: 48, display: "flex", justifyContent: "center" }}>
          <Spin size="large" />
        </div>
      );
    }
    if (rows.length === 0) {
      return (
        <ScanHistoryEmptyStatus
          t={t}
          onRunFirstScan={() => navigate("/studio/new")}
          onConfigureAlerts={() => navigate("/studio/monitoring")}
          onCompareFromEmpty={() => {
            message.warning(t.sheCompareNeedScanFirst);
            navigate("/studio/new");
          }}
        />
      );
    }
    return (
      <StudioScanRecordsPanel
        lang={lang}
        t={t}
        rows={rows}
        onOpenDetail={openDetail}
      />
    );
  }, [loading, rows, t, lang, navigate]);

  return <div className="studio-wb-records-page">{body}</div>;
}
